// Typeahead del paso «1 · Producto»: busca teléfonos por modelo y elige la variante con chips
// de capacidad y color. Lo que se cotiza sigue siendo un SKU.
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Search, Smartphone, X } from 'lucide-react';
import { ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { PromoChip } from '@/domains/sales/wizard/components/PromoChip';
import { VariantPicker } from '@/shared/patterns/VariantPicker';
import {
    findVariant,
    fromPriceLabel,
    groupByModel,
    variantCountLabel,
    variantSelector,
    type ModelGroup,
} from '../../catalog/lib/modelGroups';
import { searchPhones } from '../lib/quoterProducts';
import { productInfoLine } from '../lib/quoterFormat';

const ROW = 'grid grid-cols-[26px_1fr_auto] items-center gap-2 rounded px-2 py-1.5 text-[12px]';

export function ProductPicker({
    phones,
    selected,
    onSelect,
    onClear,
    isLoading,
    isError,
    onRetry,
}: {
    phones: CatalogItem[];
    selected: CatalogItem | null;
    onSelect: (item: CatalogItem) => void;
    onClear: () => void;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    // Sin teclear, el campo muestra el equipo elegido; al teclear manda el borrador.
    const text = query ?? (selected ? itemDisplayName(selected) : '');
    const groups = useMemo(() => groupByModel(phones), [phones]);
    const results = useMemo(() => groupByModel(searchPhones(phones, query ?? '')), [phones, query]);
    const selectedGroup = selected ? (findVariant(groups, selected.sku)?.group ?? null) : null;

    // Cerrar sin elegir descarta lo tecleado: el campo vuelve a mostrar el equipo elegido.
    const close = () => {
        setQuery(null);
        setOpen(false);
    };

    // Elegir un modelo conserva la variante elegida si es del mismo; si no, la por defecto.
    const pick = (group: ModelGroup) => {
        const keep = group.variants.find((variant) => variant.sku === selected?.sku);
        onSelect(keep ?? group.defaultVariant);
        close();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setHighlight((index) => Math.min(results.length - 1, index + 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight((index) => Math.max(0, index - 1));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const group = results.at(Math.max(highlight, 0));
            if (group) pick(group);
        } else if (event.key === 'Escape') {
            close();
        }
    };

    if (isError) {
        return (
            <ErrorState
                title="No se pudo leer el catálogo de la tienda."
                description="Vuelve a intentarlo o cotiza con un monto libre."
                onRetry={onRetry}
            />
        );
    }

    return (
        <div>
            <div
                className="relative"
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) close();
                }}
            >
                <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-activedescendant={
                        open && highlight >= 0 && highlight < results.length
                            ? optionId(listId, highlight)
                            : undefined
                    }
                    aria-autocomplete="list"
                    aria-label="Buscar marca o modelo"
                    placeholder={isLoading ? 'Cargando catálogo…' : 'Buscar marca o modelo...'}
                    autoComplete="off"
                    disabled={isLoading}
                    value={text}
                    onFocus={() => {
                        setHighlight(-1);
                        setOpen(true);
                    }}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setHighlight(-1);
                        setOpen(true);
                    }}
                    onKeyDown={onKeyDown}
                    className="h-10 w-full rounded-[10px] border border-border bg-card px-8 text-[13px] outline-none focus:border-foreground/50 disabled:opacity-60"
                />
                {selected || (query ?? '') !== '' ? (
                    <button
                        type="button"
                        aria-label="Limpiar"
                        title="Limpiar"
                        onClick={() => {
                            setQuery('');
                            onClear();
                            setOpen(true);
                            inputRef.current?.focus();
                        }}
                        className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                        <X className="size-3.5" aria-hidden />
                    </button>
                ) : null}
                {open && !isLoading ? (
                    <div
                        id={listId}
                        role="listbox"
                        aria-label="Teléfonos del catálogo"
                        className="absolute inset-x-0 top-[calc(100%+4px)] z-20 max-h-[280px] overflow-y-auto rounded-[10px] border border-border bg-card p-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
                    >
                        {results.length === 0 ? (
                            <div className="px-2.5 py-3.5 text-center text-[11.5px] text-muted-foreground">
                                {phones.length === 0
                                    ? 'Tu tienda no tiene teléfonos en el catálogo.'
                                    : `Sin resultados para "${query ?? ''}"`}
                            </div>
                        ) : (
                            results.map((group, index) => (
                                <ModelOption
                                    key={group.key}
                                    id={optionId(listId, index)}
                                    group={group}
                                    selected={group.variants.some(
                                        (variant) => variant.sku === selected?.sku,
                                    )}
                                    highlighted={index === highlight}
                                    onPick={() => {
                                        pick(group);
                                    }}
                                />
                            ))
                        )}
                    </div>
                ) : null}
            </div>
            <div className="mt-1.5 text-[11px] text-muted-foreground">
                {selected ? (
                    <>
                        <span className="font-semibold text-foreground">
                            {itemDisplayName(selected)}
                        </span>
                        {productInfoLine(selected)}
                    </>
                ) : null}
            </div>
            {selected && selectedGroup ? (
                <VariantChoice group={selectedGroup} selected={selected} onSelect={onSelect} />
            ) : null}
        </div>
    );
}

/** Un resultado por modelo: «6 variantes · desde $3,999». */
function ModelOption({
    id,
    group,
    selected,
    highlighted,
    onPick,
}: {
    id: string;
    group: ModelGroup;
    selected: boolean;
    highlighted: boolean;
    onPick: () => void;
}) {
    const item = group.defaultVariant;
    const promotion = group.variants.find((variant) => variant.promotions?.[0])?.promotions?.[0];
    return (
        <button
            id={id}
            type="button"
            role="option"
            aria-selected={selected}
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            onClick={onPick}
            className={cn(ROW, 'w-full text-left hover:bg-accent', highlighted && 'bg-accent')}
        >
            <ProductThumb item={item} />
            <span className="min-w-0">
                <span className="flex min-w-0 items-center">
                    <span
                        className="max-w-full flex-none truncate font-semibold"
                        title={itemDisplayName(item)}
                    >
                        {itemDisplayName(item)}
                    </span>
                    {promotion ? (
                        <PromoChip className="ml-1.5 min-w-0">
                            <span className="min-w-0 truncate" title={promotion.name}>
                                {promotion.name}
                            </span>
                        </PromoChip>
                    ) : null}
                </span>
                <span className="mt-px block truncate text-[10px] font-medium text-muted-foreground">
                    {`${variantCountLabel(group)} · ${fromPriceLabel(group)}`}
                </span>
            </span>
            <span className="text-[12px] font-bold tabular-nums text-primary">
                {formatMXN(group.minPriceCents / 100)}
            </span>
        </button>
    );
}

/** Selector de variante del modelo; cotizar no aparta, así que lo agotado también se elige. */
function VariantChoice({
    group,
    selected,
    onSelect,
}: {
    group: ModelGroup;
    selected: CatalogItem;
    onSelect: (item: CatalogItem) => void;
}) {
    const selector = variantSelector(group, selected);
    return (
        <div className="mt-2">
            <VariantPicker
                summary={selector.summary}
                dimensions={selector.dimensions}
                allowSoldOut
                onSelect={(sku) => {
                    const next = group.variants.find((item) => item.sku === sku);
                    if (next) onSelect(next);
                }}
            />
        </div>
    );
}

/** Id de la opción, para que el lector de pantalla siga la resaltada con el teclado. */
function optionId(listId: string, index: number): string {
    return `${listId}-option-${String(index)}`;
}

function ProductThumb({ item }: { item: CatalogItem }) {
    if (item.imageUrl) {
        return <img src={item.imageUrl} alt="" className="size-[22px] object-contain" />;
    }
    return <Smartphone className="mx-auto size-4 text-muted-foreground" aria-hidden />;
}
